import { actions } from "@/actions";
import { NoteEditorTopBarShareButton } from "@/components/note/editor/top-bar/NoteEditorTopBarShareButton";
import { OrganizeNote, useOrganizeNoteState } from "@/components/note/editor/top-bar/organize";
import { MdsDropdownIconButton } from "@/design-system/components/dropdown";
import { MdsDropdownContentList } from "@/design-system/components/dropdown";
import { MdsDropdownItemKind } from "@/design-system/components/dropdown";
import { MdsIconKind } from "@/design-system/components/icon";
import { MdsItemListRowFeaturedContent } from "@/design-system/components/item-list/rows/MdsItemListRowFeaturedContent";
import { AnimationTiming } from "@/design-system/constants/common";
import { mdsPanelWidthsValue } from "@/design-system/foundations";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import {
  MDSPanelHeaderButtonGroup,
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSEditableTitle,
  MDSTextVariant,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSIconButtonSize,
} from "@/mds";
import { TemplatesEditorBanner } from "@/pages/templates-view/components/TemplatesEditorBanner";

interface TemplateEditorTopBarProps {
  template?: TemplateObservable;
}

export const TemplateEditorTopBar = observer<TemplateEditorTopBarProps>(
  function TemplateEditorTopBar({ template }) {
    const page = useTabPageStore("templatesViewPage");
    const organizeNoteState = useOrganizeNoteState();

    const templateTitleInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
      if (template && !template.title) templateTitleInputRef.current?.focus();
    }, [template]);

    useEffect(() => {
      // Make sure to save the template when the component unmounts because blur is not guaranteed
      // to be called when navigating away from the page.
      return () => {
        if (template && !template.isDeleted) template.save();
      };
    }, [template]);

    if (!template) return null;

    const dropdownContentList: MdsDropdownContentList = {
      items: [
        {
          id: "copy-template-link",
          kind: MdsDropdownItemKind.Button,
          label: "Copy link",
          iconKind: MdsIconKind.Link,
          onClick: () => actions.copyTemplateLinkToClipboard({ templateId: template.id }),
        },
        {
          id: "delete-template",
          kind: MdsDropdownItemKind.Button,
          label: "Delete",
          iconKind: MdsIconKind.Trash,
          onClick: () =>
            page.deleteTemplateModal.open({
              templateId: template.id,
            }),
        },
      ],
    };

    return (
      <Wrapper>
        <MDSPanelHeader>
          <MDSEditableTitle
            variant={MDSTextVariant.HeaderM}
            slot={MDSPanelHeaderSlotName.Heading}
            value={template.title}
            placeholder={"Template name"}
            onFocus={() => template.lock()}
            onChange={e => template.setTitle(e.currentTarget.value)}
            onBlur={async () => await template.save()}
            autoFocus={template?.title === ""}
            readOnly={!template.canWrite}
          />
          <StyledSubsection slot={MDSPanelHeaderSlotName.Subsection}>
            {!!template.collectionList.allCollections.length && (
              <MdsItemListRowFeaturedContent
                collections={sortCollectionsForChips(template.collectionList.allCollections)}
              />
            )}
          </StyledSubsection>
          <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
            <OrganizeNote note={template} {...organizeNoteState}>
              <MDSTooltip
                config={{
                  label: "Organize",
                  placement: MDSTooltipPlacement.Top,
                  delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
                }}
              >
                <MDSIconButton
                  iconKind={MdsIconKind.Collection}
                  data-test-id="note-editor-top-bar-organize-button"
                  onClick={organizeNoteState.toggleOrganizeIsOpen}
                  size={MDSIconButtonSize.Medium}
                />
              </MDSTooltip>
            </OrganizeNote>
            <NoteEditorTopBarShareButton note={template} shareSheetModal={page.shareSheetModal} />
            <MdsDropdownIconButton
              placement="below-right-alignment"
              contentList={dropdownContentList}
              size={MDSIconButtonSize.Medium}
            />
          </MDSPanelHeaderButtonGroup>
        </MDSPanelHeader>
        <TemplatesEditorBanner template={template} />
      </Wrapper>
    );
  }
);

const Wrapper = styled.div({});

const StyledSubsection = styled.div`
  padding-top: ${({ theme }) => theme.spacing["3"]};
  padding-bottom: ${({ theme }) => theme.spacing["2"]};

  @container mds-panel (max-width: ${mdsPanelWidthsValue.headerBreakpoint}px) {
    padding-top: 0px;
    padding-left: 12px;
  }
`;
